import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Phone, 
  MessageSquare, 
  Send, 
  UserCheck,
  Eye,
  RefreshCw,
  FileText,
  XCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Wrench,
  ChevronDown,
  MoreHorizontal,
  Shield,
  Camera
} from 'lucide-react';
import EvidencePromptDialog from './EvidencePromptDialog';
import { 
  WORKFLOW_STAGES, 
  checkEvidenceCompleteness,
  getWorkflowStageFromStatus
} from '@/config/evidenceRules';

const API = process.env.REACT_APP_BACKEND_URL;

// Actions that require evidence enforcement
const EVIDENCE_ENFORCED_ACTIONS = {
  'mark_in_progress': WORKFLOW_STAGES.START_WORK,
  'mark_complete': WORKFLOW_STAGES.MARK_COMPLETE,
  'approve_completion': WORKFLOW_STAGES.MARK_COMPLETE,
  'submit_invoice': WORKFLOW_STAGES.SUBMIT_INVOICE,
  'upload_invoice': WORKFLOW_STAGES.SUBMIT_INVOICE,
  'close_job': WORKFLOW_STAGES.CLOSE_JOB
};

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Status-based action configurations
const STATUS_ACTIONS = {
  'new': [
    { id: 'assign', label: 'Assign Contractor', desc: 'Assign to a contractor', icon: UserCheck, color: 'bg-emerald-600 hover:bg-emerald-700' },
    { id: 'add_note', label: 'Add Internal Note', desc: 'Add a note visible to internal team', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'cancel', label: 'Cancel Job', desc: 'Cancel or close this job', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' }
  ],
  'assigned': [
    { id: 'chase', label: 'Chase Contractor', desc: 'Send reminder to submit quote', icon: Send, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'mark_awaiting', label: 'Mark Awaiting Quote', desc: 'Contractor is preparing quote', icon: Clock, color: 'bg-amber-600 hover:bg-amber-700' },
    { id: 'mark_in_progress', label: 'Mark In Progress', desc: 'Works have started', icon: Wrench, color: 'bg-teal-600 hover:bg-teal-700', evidenceEnforced: true },
    { id: 'view_contractor', label: 'View Contractor Details', desc: 'View profile, performance & history', icon: Eye, color: 'bg-slate-600 hover:bg-slate-700' },
    { id: 'reassign', label: 'Reassign Contractor', desc: 'Assign to a different contractor', icon: RefreshCw, color: 'bg-purple-600 hover:bg-purple-700' },
    { id: 'add_note', label: 'Add Internal Note', desc: 'Add a note visible to internal team', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'cancel', label: 'Cancel Job', desc: 'Cancel or close this job', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' }
  ],
  'awaiting_quote': [
    { id: 'chase', label: 'Chase Quote', desc: 'Send reminder to submit quote', icon: Send, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'upload_quote', label: 'Upload Quote Manually', desc: 'Add quote on behalf of contractor', icon: FileText, color: 'bg-teal-600 hover:bg-teal-700' },
    { id: 'reassign', label: 'Reassign Contractor', desc: 'Assign to a different contractor', icon: RefreshCw, color: 'bg-purple-600 hover:bg-purple-700' },
    { id: 'add_note', label: 'Add Internal Note', desc: 'Add a note visible to internal team', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'cancel', label: 'Cancel Job', desc: 'Cancel or close this job', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' }
  ],
  'quote_submitted': [
    { id: 'review_quote', label: 'Review Quote', desc: 'Review and approve/reject quote', icon: Eye, color: 'bg-emerald-600 hover:bg-emerald-700' },
    { id: 'approve_quote', label: 'Approve Quote', desc: 'Approve this quote', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700' },
    { id: 'reject_quote', label: 'Reject Quote', desc: 'Reject and request revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' },
    { id: 'add_note', label: 'Add Technical Note', desc: 'Add technical review notes', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' }
  ],
  'approved': [
    { id: 'issue_po', label: 'Issue PO', desc: 'Generate purchase order', icon: FileText, color: 'bg-emerald-600 hover:bg-emerald-700' },
    { id: 'schedule', label: 'Schedule Works', desc: 'Set date for works', icon: Clock, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'mark_in_progress', label: 'Mark In Progress', desc: 'Works have started', icon: Wrench, color: 'bg-teal-600 hover:bg-teal-700', evidenceEnforced: true },
    { id: 'add_note', label: 'Add Contractor Note', desc: 'Add note for contractor', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' }
  ],
  'in_progress': [
    { id: 'upload_evidence', label: 'Upload Evidence', desc: 'Add photos/documents', icon: Camera, color: 'bg-teal-600 hover:bg-teal-700' },
    { id: 'mark_complete', label: 'Mark Complete', desc: 'Mark works as complete', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700', evidenceEnforced: true },
    { id: 'add_update', label: 'Add Site Update', desc: 'Log progress update', icon: MessageSquare, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'raise_followon', label: 'Raise Follow-On', desc: 'Create follow-on job', icon: RefreshCw, color: 'bg-purple-600 hover:bg-purple-700' },
    { id: 'escalate', label: 'Escalate Issue', desc: 'Flag for urgent attention', icon: AlertTriangle, color: 'bg-amber-600 hover:bg-amber-700' }
  ],
  'completed': [
    { id: 'review_evidence', label: 'Review Evidence', desc: 'Check completion evidence', icon: Shield, color: 'bg-teal-600 hover:bg-teal-700' },
    { id: 'approve_completion', label: 'Approve Completion', desc: 'Sign off works as complete', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700', evidenceEnforced: true },
    { id: 'request_evidence', label: 'Request More Evidence', desc: 'Ask for additional photos', icon: Camera, color: 'bg-amber-600 hover:bg-amber-700' },
    { id: 'raise_defects', label: 'Raise Defects', desc: 'Report quality issues', icon: AlertTriangle, color: 'bg-red-600 hover:bg-red-700' }
  ],
  'awaiting_invoice': [
    { id: 'request_invoice', label: 'Request Invoice', desc: 'Chase contractor for invoice', icon: Send, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'upload_invoice', label: 'Upload Invoice', desc: 'Add invoice manually', icon: FileText, color: 'bg-teal-600 hover:bg-teal-700', evidenceEnforced: true },
    { id: 'submit_invoice', label: 'Submit For Payment', desc: 'Submit invoice for payment', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700', evidenceEnforced: true },
    { id: 'chase_invoice', label: 'Chase Invoice', desc: 'Send reminder', icon: Send, color: 'bg-amber-600 hover:bg-amber-700' }
  ],
  'invoiced': [
    { id: 'approve_invoice', label: 'Approve Invoice', desc: 'Approve for payment', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700' },
    { id: 'reject_invoice', label: 'Reject Invoice', desc: 'Reject and request revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' },
    { id: 'send_accounts', label: 'Send To Accounts', desc: 'Forward to finance team', icon: Send, color: 'bg-blue-600 hover:bg-blue-700' }
  ],
  'paid': [
    { id: 'close_job', label: 'Close Job', desc: 'Mark as fully complete', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700', evidenceEnforced: true },
    { id: 'export_pack', label: 'Export Job Pack', desc: 'Download all documents', icon: FileText, color: 'bg-slate-600 hover:bg-slate-700' }
  ]
};

// Get normalized status key
const getStatusKey = (status) => {
  if (!status) return 'new';
  const normalized = status.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  
  // Map to our status keys
  if (['new', 'enquiry', 'job_created'].includes(normalized)) return 'new';
  if (['assigned', 'pending_acceptance'].includes(normalized)) return 'assigned';
  if (['awaiting_quote', 'quote_requested'].includes(normalized)) return 'awaiting_quote';
  if (['quote_submitted', 'quote_received', 'quote_awaiting_review'].includes(normalized)) return 'quote_submitted';
  if (['approved', 'quote_approved', 'po_issued'].includes(normalized)) return 'approved';
  if (['in_progress', 'works_in_progress', 'scheduled'].includes(normalized)) return 'in_progress';
  if (['completed', 'awaiting_review', 'completed_awaiting_review'].includes(normalized)) return 'completed';
  if (['awaiting_invoice'].includes(normalized)) return 'awaiting_invoice';
  if (['invoiced', 'invoice_submitted'].includes(normalized)) return 'invoiced';
  if (['paid', 'closed'].includes(normalized)) return 'paid';
  
  return 'new';
};

export default function TakeActionPanel({ 
  job, 
  onAction,
  onUploadEvidence,
  className = "" 
}) {
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(null);
  
  // Evidence prompt dialog state
  const [evidencePromptOpen, setEvidencePromptOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingStage, setPendingStage] = useState(null);
  
  // Compute derived values (empty if no job)
  const statusKey = job ? getStatusKey(job.status) : 'new';
  const actions = STATUS_ACTIONS[statusKey] || STATUS_ACTIONS['new'];
  
  // Split primary (first 5) and secondary actions
  const primaryActions = actions.slice(0, 5);
  const secondaryActions = actions.slice(5);
  
  // Check evidence completeness for the job
  const checkEvidenceForAction = useCallback((actionId) => {
    if (!job) return { canProceed: true, hardBlocks: [], softWarnings: [] };
    const stage = EVIDENCE_ENFORCED_ACTIONS[actionId];
    if (!stage) return { canProceed: true, hardBlocks: [], softWarnings: [] }; // No enforcement for this action
    
    const jobType = job.job_type || job.category || 'default';
    const evidence = job.evidence_files || [];
    return checkEvidenceCompleteness(evidence, jobType, stage);
  }, [job]);
  
  // Execute action helper
  const executeAction = useCallback(async (actionId) => {
    setLoading(actionId);
    try {
      await onAction?.(actionId, job);
    } finally {
      setLoading(null);
    }
  }, [onAction, job]);
  
  const handleAction = useCallback(async (actionId) => {
    // Check if this action requires evidence enforcement
    const stage = EVIDENCE_ENFORCED_ACTIONS[actionId];
    if (stage) {
      const completeness = checkEvidenceForAction(actionId);
      
      // If there are hard blocks OR soft warnings, show the prompt dialog
      if (completeness.hardBlocks.length > 0 || completeness.softWarnings.length > 0) {
        setPendingAction(actionId);
        setPendingStage(stage);
        setEvidencePromptOpen(true);
        return;
      }
    }
    
    // No enforcement needed, proceed directly
    executeAction(actionId);
  }, [checkEvidenceForAction, executeAction]);
  
  const handleEvidenceProceed = useCallback(() => {
    if (pendingAction) {
      executeAction(pendingAction);
      setPendingAction(null);
      setPendingStage(null);
    }
  }, [pendingAction, executeAction]);
  
  const handleUploadFromPrompt = useCallback((category) => {
    // Close the dialog and trigger evidence upload
    setEvidencePromptOpen(false);
    onUploadEvidence?.(category);
  }, [onUploadEvidence]);
  
  // Early return AFTER all hooks
  if (!job) return null;
  
  // Quick Contact helpers
  const residentPhone = job.resident_phone || job.contact_phone || job.phone;
  const residentName = job.resident_name || job.contact_name || 'Resident';
  const contractorPhone = job.contractor_phone || job.assigned_contractor?.phone;
  const contractorName = job.contractor_name || job.assigned_contractor?.company_name || 'Contractor';
  
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('0')) return '44' + digits.substring(1);
    if (digits.startsWith('44')) return digits;
    return '44' + digits;
  };
  
  const openWhatsApp = (phone, name, type) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return;
    const message = type === 'resident'
      ? `Hello ${name}, this is Park Home Support regarding job ${job.reference || job.id}. We have an update for you.`
      : `Hello, this is Park Home Support regarding job ${job.reference || job.id}. Please provide an update when possible.`;
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`} data-testid="take-action-panel">
      {/* Header */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100 bg-slate-50">
        <h3 className="font-semibold text-slate-800 text-sm">Take Action</h3>
      </div>
      
      {/* Primary Actions - MOBILE OPTIMIZED touch targets */}
      <div className="p-2.5 sm:p-3 space-y-2">
        {primaryActions.map((action) => {
          const Icon = action.icon;
          const hasEvidenceEnforcement = action.evidenceEnforced;
          const evidenceStatus = hasEvidenceEnforcement ? checkEvidenceForAction(action.id) : null;
          const hasIssues = evidenceStatus && (evidenceStatus.hardBlocks.length > 0 || evidenceStatus.softWarnings.length > 0);
          
          return (
            <button
              key={action.id}
              onClick={() => handleAction(action.id)}
              disabled={loading === action.id}
              className={`w-full flex items-start gap-3 p-3.5 sm:p-3 rounded-xl text-left transition touch-manipulation active:scale-[0.98] ${action.color} text-white relative`}
              data-testid={`action-${action.id}`}
            >
              <Icon className="h-5 w-5 sm:h-4 sm:w-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{action.label}</span>
                  {hasEvidenceEnforcement && hasIssues && (
                    <Shield className="h-3.5 w-3.5 text-white/70" title="Evidence check required" />
                  )}
                </div>
                <div className="text-[11px] opacity-80">{action.desc}</div>
              </div>
              {hasEvidenceEnforcement && evidenceStatus?.hardBlocks.length > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-300 animate-pulse" title="Missing required evidence" />
              )}
            </button>
          );
        })}
        
        {/* More Actions Dropdown */}
        {secondaryActions.length > 0 && (
          <button
            onClick={() => setShowMore(!showMore)}
            className="w-full flex items-center justify-center gap-2 p-3 sm:p-2.5 rounded-xl border border-slate-200 text-slate-600 active:bg-slate-100 transition text-sm touch-manipulation"
          >
            <MoreHorizontal className="h-4 w-4" />
            More Actions
            <ChevronDown className={`h-4 w-4 transition ${showMore ? 'rotate-180' : ''}`} />
          </button>
        )}
        
        {showMore && secondaryActions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              onClick={() => handleAction(action.id)}
              disabled={loading === action.id}
              className={`w-full flex items-start gap-3 p-3.5 sm:p-3 rounded-xl text-left transition touch-manipulation active:scale-[0.98] ${action.color} text-white`}
            >
              <Icon className="h-5 w-5 sm:h-4 sm:w-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{action.label}</div>
                <div className="text-[11px] opacity-80">{action.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Quick Contact Section - MOBILE OPTIMIZED */}
      <div className="px-3 sm:px-4 py-3 border-t border-slate-100 bg-slate-50">
        <h4 className="text-xs font-semibold text-slate-600 mb-3">Quick Contact</h4>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Resident Contact */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <User className="h-3 w-3 text-slate-400" />
              <span className="text-[11px] font-medium text-slate-600">Resident</span>
            </div>
            <div className="text-xs text-slate-700 mb-1.5 truncate">{residentName}</div>
            <div className="text-[10px] text-slate-500 mb-2">{residentPhone || 'No phone'}</div>
            
            <div className="flex flex-wrap gap-1">
              {residentPhone && (
                <>
                  <button
                    onClick={() => window.location.href = `tel:${residentPhone}`}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 text-[10px] text-slate-600 hover:bg-slate-50"
                  >
                    <Phone className="h-2.5 w-2.5" /> Call
                  </button>
                  <button
                    onClick={() => openWhatsApp(residentPhone, residentName, 'resident')}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-[10px] text-emerald-600 hover:bg-emerald-50"
                  >
                    <WhatsAppIcon className="h-2.5 w-2.5" /> WhatsApp
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Contractor Contact */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Wrench className="h-3 w-3 text-slate-400" />
              <span className="text-[11px] font-medium text-slate-600">Contractor</span>
            </div>
            <div className="text-xs text-slate-700 mb-1.5 truncate">{contractorName}</div>
            <div className="text-[10px] text-slate-500 mb-2">{contractorPhone || 'Not assigned'}</div>
            
            <div className="flex flex-wrap gap-1">
              {contractorPhone && (
                <>
                  <button
                    onClick={() => window.location.href = `tel:${contractorPhone}`}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 text-[10px] text-slate-600 hover:bg-slate-50"
                  >
                    <Phone className="h-2.5 w-2.5" /> Call
                  </button>
                  <button
                    onClick={() => openWhatsApp(contractorPhone, contractorName, 'contractor')}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-[10px] text-emerald-600 hover:bg-emerald-50"
                  >
                    <WhatsAppIcon className="h-2.5 w-2.5" /> WhatsApp
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Evidence Prompt Dialog */}
      <EvidencePromptDialog
        open={evidencePromptOpen}
        onOpenChange={setEvidencePromptOpen}
        job={job}
        stage={pendingStage}
        onProceed={handleEvidenceProceed}
        onUploadEvidence={handleUploadFromPrompt}
      />
    </div>
  );
}

export { STATUS_ACTIONS, getStatusKey, EVIDENCE_ENFORCED_ACTIONS };
